import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import UsageRing from "@/components/UsageRing";
import { History, LogOut, Menu, X, CreditCard, BookOpen, User, Download, Wrench } from "lucide-react";

export default function Navbar({ usage }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinks = [
    { path: "/app", label: "Dashboard", testid: "nav-dashboard" },
    { path: "/app/library", label: "Library", icon: BookOpen, testid: "nav-library" },
    { path: "/ai-tools", label: "AI Tools", icon: Wrench, testid: "nav-ai-tools" },
    { path: "/downloads", label: "Downloads", icon: Download, testid: "nav-downloads" },
  ];

  return (
    <nav className="sticky top-0 z-50" style={{ background: "rgba(250,250,248,0.8)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderBottom: "1px solid rgba(232,228,220,0.6)" }}>
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="font-display text-2xl font-bold cursor-pointer" style={{ color: "var(--rolex-green)" }} onClick={() => navigate("/")} data-testid="navbar-logo">Buddy</h1>
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                style={{ color: location.pathname === link.path ? "var(--rolex-green)" : "var(--text-muted)", background: location.pathname === link.path ? "var(--rolex-green-light)" : "transparent" }}
                data-testid={link.testid}
              >
                {link.icon && <link.icon size={14} />}
                {link.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {usage && (
            <div className="hidden sm:flex items-center gap-2">
              <UsageRing used={usage.rewrites_used} limit={usage.rewrites_limit} size={28} strokeWidth={3} fontSize={8} />
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>{usage.rewrites_limit === -1 ? "Unlimited" : `${usage.rewrites_used}/${usage.rewrites_limit}`}</span>
            </div>
          )}
          {/* User avatar with dropdown */}
          <button
            onClick={() => navigate("/app/account")}
            className="hidden md:flex items-center gap-2 px-2 py-1 rounded-md hover:bg-[var(--surface-warm)] transition-colors"
            data-testid="nav-account"
          >
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }}>
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>{user?.name?.split(" ")[0]}</span>
          </button>
          <button onClick={handleLogout} className="hidden md:flex items-center gap-1 text-xs px-3 py-1.5 rounded-md hover:bg-[var(--surface-warm)] transition-colors" style={{ color: "var(--text-muted)" }} data-testid="nav-logout">
            <LogOut size={13} /> Sign Out
          </button>
          <button className="md:hidden p-1.5" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t px-4 py-3 space-y-1 animate-fadeIn" style={{ borderColor: "var(--surface-border)", background: "var(--surface-white)" }}>
          {navLinks.map((link) => (
            <button key={link.path} onClick={() => { navigate(link.path); setMenuOpen(false); }} className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-sm"
              style={{ color: location.pathname === link.path ? "var(--rolex-green)" : "var(--text-secondary)" }}>
              {link.icon && <link.icon size={14} />}{link.label}
            </button>
          ))}
          <button onClick={() => { navigate("/app/account"); setMenuOpen(false); }} className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-sm" style={{ color: "var(--text-secondary)" }}>
            <User size={14} /> Account
          </button>
          <button onClick={handleLogout} className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-sm" style={{ color: "var(--error)" }}>
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      )}
    </nav>
  );
}
